import type {
  LookupServiceResponseType,
  HamDbResponseType,
  HamQthResponseType,
  LookupServiceProviderType,
  QrzLookupResponseType
} from "@/types";

import HamDbResponseToLogbookResponseTransformer from "@/transformers/HamDbResponseToLogbookResponseTransformer";
import HamQthResponseToLogbookResponseTransformer from "@/transformers/HamQthResponseToLogbookResponseTransformer";
import QrzResponseToLogbookResponseTransformer from "@/transformers/QrzResponseToLogbookResponseTransformer";

import { captureMessage } from "@/utils/sentry";
import { trimObjectValues } from "@/utils/formatters";
import { resolveCanonicalCountry, isKnownDxccEntity } from "@/utils/dxccEntityMap";

type TransformFunctionType = {
  [key in LookupServiceProviderType]: (response: any) => LookupServiceResponseType;
};

type ResponseType = HamDbResponseType | HamQthResponseType | QrzLookupResponseType

const transformFunctions: TransformFunctionType = {
  HAMDB: HamDbResponseToLogbookResponseTransformer,
  HAMQTH: HamQthResponseToLogbookResponseTransformer,
  QRZ: QrzResponseToLogbookResponseTransformer,
  OFFLINE: (response: LookupServiceResponseType) => response, // Identity function for offline data
};

function safeTrim(value: any, defaultValue: string = ''): string {
  return typeof value === 'string' ? value.trim() : defaultValue;
}

function safeToUpperCase(value: any, defaultValue: string = ''): string {
  return typeof value === 'string' ? value.toUpperCase() : defaultValue;
}

function normalizeCommonFields(
  response: Partial<LookupServiceResponseType>
): LookupServiceResponseType {
  const trimmedResponse = trimObjectValues(response) as LookupServiceResponseType;

  return {
    ...trimmedResponse,
    callsign: safeToUpperCase(trimmedResponse.callsign),
    fullName: safeTrim(trimmedResponse.fullName),
    firstName: safeTrim(trimmedResponse.firstName),
    lastName: safeTrim(trimmedResponse.lastName),
    address1: safeToUpperCase(trimmedResponse.address1),
    address2: safeToUpperCase(trimmedResponse.address2),
    city: safeToUpperCase(trimmedResponse.city),
    country: resolveCanonicalCountry(trimmedResponse.dxcc, trimmedResponse.country),
    county: safeToUpperCase(trimmedResponse.county),
  };
}

export default function LookupServiceResponseTransformer(
  response: ResponseType,
  service: LookupServiceProviderType
): LookupServiceResponseType {
  const transformFunction = transformFunctions[service];

  if (!transformFunction) {
    throw new Error(`No transform function defined for service: ${service}`);
  }

  const transformResponse = transformFunction(response);
  const normalized = normalizeCommonFields(transformResponse);

  // Report unrecognized country names from lookup services so we can add aliases.
  // Skip values with no letters (e.g. "0", "86") -- those are malformed lookup
  // data, not country names we could ever alias, so they're just noise.
  const looksLikeCountryName = /\p{L}/u.test(normalized.country ?? "");
  if (
    normalized.country &&
    !normalized.dxcc &&
    looksLikeCountryName &&
    !isKnownDxccEntity(normalized.country)
  ) {
    captureMessage(`Unrecognized DXCC country name from ${service}: ${normalized.country}`, {
      level: "warning",
      fingerprint: ["dxcc-normalization", normalized.country!],
      tags: {
        feature: "dxcc-normalization",
        "lookup-service": service,
      },
      extra: {
        originalCountry: transformResponse.country,
        normalizedCountry: normalized.country,
        callsign: normalized.callsign,
      },
    });
  }

  return normalized;
}
